import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LOGO_URL, IMAGES } from "@/data/constants";
import { ProgereText } from "@/components/ProgereText";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { ArrowRight, LayoutGrid, PlayCircle } from "lucide-react";

export function HeroSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pg-keep-dark" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt=""
          className="w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#050505]" />
        <div className="absolute inset-0 pg-grid-bg opacity-60" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] pg-radial-red" />
        <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] pg-radial-gold" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-28 pb-20 w-full">
        <div className="max-w-3xl">
          <div className="pg-fade-up flex items-center gap-3 mb-8" style={{ animationDelay: "0.05s" }}>
            <img src={LOGO_URL} alt="PROGÈRE" className="h-16 w-16 object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" />
            <span className="inline-flex items-center gap-2 rounded-full border border-[#EAB308]/30 bg-[#EAB308]/5 px-4 py-1.5 text-xs font-mono uppercase tracking-[0.2em] text-[#EAB308]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EAB308] animate-pulse" />
              {t("hero.badge")}
            </span>
          </div>

          <h1 className="pg-fade-up text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]" style={{ animationDelay: "0.15s" }}>
            <ProgereText />
          </h1>

          <p className="pg-fade-up mt-5 text-2xl sm:text-3xl font-bold text-white leading-tight max-w-2xl" style={{ animationDelay: "0.2s" }}>
            {t("hero.title2a")}{" "}
            <span className="text-[#EAB308]">{t("hero.title2b")}</span>
          </p>

          <p className="pg-fade-up mt-5 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed" style={{ animationDelay: "0.25s" }}>
            {t("hero.desc")}
          </p>

          <div className="pg-fade-up mt-10 flex flex-col sm:flex-row gap-4" style={{ animationDelay: "0.35s" }}>
            <Button
              data-testid="hero-space-btn"
              size="lg"
              onClick={() => navigate(user ? "/app" : "/login")}
              className="h-12 px-7 bg-[#EAB308] text-black font-semibold text-base rounded-md gap-2 hover:opacity-90 transition-opacity"
            >
              {t("hero.ctaSpace")}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              data-testid="hero-modules-btn"
              size="lg"
              variant="outline"
              onClick={() => document.getElementById("modules")?.scrollIntoView({ behavior: "smooth" })}
              className="h-12 px-7 border-white/20 bg-white/5 text-white hover:border-[#EAB308]/60 hover:bg-white/10 text-base rounded-md gap-2 transition-colors"
            >
              <LayoutGrid className="w-5 h-5" />
              {t("hero.ctaModules")}
            </Button>
            <Button
              data-testid="hero-demo-btn"
              size="lg"
              variant="ghost"
              onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
              className="h-12 px-7 text-white/80 hover:text-white hover:bg-white/5 text-base rounded-md gap-2 transition-colors"
            >
              <PlayCircle className="w-5 h-5" />
              {t("hero.ctaDemo")}
            </Button>
          </div>

          <div className="pg-fade-up mt-14 grid grid-cols-3 gap-6 max-w-lg" style={{ animationDelay: "0.45s" }}>
            {[
              { n: "6", l: t("hero.stat1") },
              { n: "1", l: t("hero.stat2") },
              { n: "∞", l: t("hero.stat3") },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-3xl font-extrabold text-[#EAB308] font-mono">{s.n}</div>
                <div className="text-xs text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
